import { useState, useCallback } from 'react';
import { collection, query, orderBy, getDocs, getFirestore, where, doc, getDoc } from 'firebase/firestore';
import logoTransparent from '../assets/logo_transparent.png';
import moment from 'moment';
import { useAppStore } from '../store';
import { selectCurrentUserRecord } from '../store/common/userRecord/slice';
import { formatDate } from '../utils/dateUtils';

export const useFeedData = (initialPosts = []) => {
    const userRecord = useAppStore(selectCurrentUserRecord);
  
  const [hasFetched, setHasFetched] = useState(false);
  const [posts, setPosts] = useState(initialPosts);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  const formatPost = (doc) => {
    const data = doc.data();
    // Skip posts without createdAt
    if (!data.createdAt) return null;
    
    const createdAt = data.createdAt;
    const updatedAt = data.updatedAt || data.createdAt;
    
    // Format dates using dateUtils
    const formattedCreatedAt = formatDate(createdAt);
    const formattedUpdatedAt = formatDate(updatedAt);
    
    // If date formatting failed, skip this post
    if (!formattedCreatedAt) return null;
    
    return {
      id: doc.id,
      ...data,
      shopType: Array.isArray(data.shopType) ? data.shopType : [data.business || 'General'],
      createdAt: formattedCreatedAt,
      updatedAt: formattedUpdatedAt,
      status: data.status || 'published',
      byline: data.byline || 'Anonymous',
      authorId: data.authorId || 'unknown',
      tags: Array.isArray(data.tags) ? data.tags : [],
      sections: Array.isArray(data.sections) ? data.sections : [],
      templateType: data.templateType || 'GENERAL',
      business: data.business || 'General',
      introduction: data.introduction || '',
      _thumbnailPreview: data._thumbnailPreview || null,
      thumbnail: data.thumbnail || null,
      // Keep original timestamps for sorting
      _rawCreatedAt: data.createdAt,
      _rawUpdatedAt: updatedAt
    };
  };

  const formatPostForFeed = (post) => {
    // Format timestamp to a readable format
    const formatTimestamp = (timestamp) => {
      if (!timestamp) return 'Just now';
      
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
      const now = new Date();
      const diffInSeconds = Math.floor((now - date) / 1000);
      
      if (diffInSeconds < 60) return 'Just now';
      if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
      if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
      return date.toLocaleDateString();
    };

    return {
      id: post.id,
      businessName: post.shopName || 'Unknown Business',
      businessLogo: post.storeImage || logoTransparent,
      postTitle: post.title || 'Unknown Title',
      postContent: post.introduction || '',
      timestamp: formatTimestamp(post.createdAt || post.updatedAt),
      thumbnail: post.thumbnail,
      likes: post.likes || 0,
      comments: post.comments || 0,
      shares: post.shares || 0,
      _raw: post
    };
  };

  const fetchBusinessDetails = async (authorId) => {
    try {
      const db = getFirestore();
      const businessDoc = await getDoc(doc(db, 'businessAtAGlance', authorId));
      
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        return {
          shopName: data.shopName || 'Unknown Shop',
          storeImage: data.storeImage || null,
          markerPosition: data.markerPosition || null
        };
      }
      return {
        shopName: 'Unknown Shop',
        storeImage: null,
        markerPosition: null
      };
    } catch (error) {
      console.error('Error fetching business details:', error);
      return {
        shopName: 'Unknown Shop',
        storeImage: null,
        markerPosition: null
      };
    }
  };

  const fetchInitialPosts = useCallback(async () => {
    if (loading) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const db = getFirestore();
      
      // First, fetch all articles with createdAt
      const articlesRef = collection(db, 'articles');
      const articlesQuery = query(
        articlesRef,
        where('createdAt', '!=', null),
        orderBy('createdAt', 'desc')
      );
  
      const articlesSnapshot = await getDocs(articlesQuery);
      
      // Get unique author IDs from all posts
      const authorIds = new Set();
      const postsData = [];
      
      // First pass: collect all author IDs and basic post data
      articlesSnapshot.docs.forEach(doc => {
        const post = formatPost(doc);
        if (post && post.authorId) {
          authorIds.add(post.authorId);
          postsData.push({
            ...post,
            businessName: 'Loading...',
            businessLogo: logoTransparent // Use default logo initially
          });
        }
      });
      
      // Fetch all business details in a single batch
      const businessDetailsPromises = Array.from(authorIds).map(async (authorId) => {
        try {
          const businessDoc = await getDoc(doc(db, 'businessAtAGlance', authorId));
          if (businessDoc.exists()) {
            const data = businessDoc.data();
            return {
              authorId,
              shopName: data.shopName || 'Unknown Business',
              storeImage: data.storeImage || null
            };
          }
        } catch (error) {
          console.error(`Error fetching business details for author ${authorId}:`, error);
        }
        return {
          authorId,
          shopName: 'Unknown Business',
          storeImage: null
        };
      });
      
      // Wait for all business details to load
      const businessDetailsList = await Promise.all(businessDetailsPromises);
      
      // Create a map of authorId -> business details
      const businessDetailsMap = businessDetailsList.reduce((acc, curr) => ({
        ...acc,
        [curr.authorId]: {
          shopName: curr.shopName,
          storeImage: curr.storeImage || logoTransparent // Use default logo if no store image
        }
      }), {});
      
      // Merge business details with posts
      const postsWithBusinessInfo = postsData.map(post => {
        const businessInfo = businessDetailsMap[post.authorId] || {
          shopName: 'Unknown Business',
          storeImage: logoTransparent // Use default logo for unknown businesses
        };
        
        return {
          ...post,
          businessName: businessInfo.shopName,
          businessLogo: businessInfo.storeImage,
          timestamp: formatDate(post._rawCreatedAt, { format: 'relative' }) || 'Just now',
          _raw: {
            ...post,
            createdAt: post._rawCreatedAt,
            updatedAt: post._rawUpdatedAt
          }
        };
      });
      
      // If no posts found, wait 3 seconds and try again
      if (postsWithBusinessInfo.length === 0) {
        console.log('No posts found, retrying in 3 seconds...');
        await new Promise(resolve => setTimeout(resolve, 3000));
        await fetchInitialPosts();
      } else {
        // Only update state if we have posts
        setPosts(postsWithBusinessInfo);
        setHasMore(false);
        setHasFetched(true);
      }
    } catch (err) {
      console.error('Error fetching posts:', err);
      setError('Failed to load posts. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [loading, userRecord?.uid]);

  const refreshPosts = useCallback(async () => {
    setPosts(initialPosts);
    await fetchInitialPosts();
  }, [initialPosts, fetchInitialPosts]);

  const fetchPosts = useCallback(async () => {
    if (hasFetched) return;
    await fetchInitialPosts();
  }, [fetchInitialPosts, hasFetched]);

  return {
    posts,
    loading,
    error,
    hasMore,
    refreshPosts,
    fetchPosts,
    hasFetched
  };
};

export default useFeedData;
